import { FieldTimeOutlined, SwapOutlined } from "@ant-design/icons";
import {
    Button,
    DatePicker,
    List,
    Result,
    Select,
    Switch,
    message,
    notification,
    Tooltip,
} from "antd";
import { Logger } from "frontend-lib/Log";
import { isMobileView } from "frontend-lib/Utils/Mobile";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CTA from "../../Constants";
import { api, serverUrl } from "../../Requests/Api";
import audio from "../../Sounds/notification.mp3";
import { useAppContext } from "../../State/Global/AppContext";
import LoadMore from "../LoadMore/LoadMore";
import LoadMoreWithExtendedDate from "../LoadMoreWithExtendedDate/LoadMoreWithExtendedDate";
import ChatItem from "./ChatItem/ChatItem";
import "./ChatList.antd.scss";
import styles from "./ChatList.module.scss";
import ChatListFilterHeaders from "./ChatListFilterHeaders";
import { ChatListHeaders } from "./ChatListHeader";
import { ChatItemSkeleton } from "./ChatItem/ChatItem";
import { useNavigate } from "react-router-dom";
import useAllUsers from "Hooks/useAllUsers";
import DerivateModal from "./DerivateModal";
import SelectGroupModal from "./SelectGroupModal";
import {
    extractChatIdFromUrl,
    fetchChats,
    filterChatByChannel,
    filterChatByOwner,
    filterChatByStatus,
    filterChatByWhichAreWaitingAnswer,
    sortByConfigTime,
    filterConfigOnlyActive,
    filterConfigAll,
} from "./chatlistfunctions";
import { addDaysToDatetime, formatDatetime } from "./dateutils";

const {
    chatListView,
    filtersDropdown,
    filtersMainContainer,
    filtersMainContainer__visible,
    filtersInnerContainer,
    filtersInnerContainer__visible,
    filterConversationTag,
} = styles;
const { Option } = Select;
const { RangePicker } = DatePicker;
const notiAudio = new Audio(audio);

function ChatList({ className, active, openChatCb, notificationSocket, logoutCb }) {
    const setInitialFilters = (ignoreDeepLink = true, useActiveInMe) => {
        const deepLinkChatId = extractChatIdFromUrl(window.location.href);
        const initialFilters = useActiveInMe
            ? filterConfigOnlyActive(deepLinkChatId)
            : filterConfigAll(deepLinkChatId);
        if (deepLinkChatId && !ignoreDeepLink) {
            initialFilters.status = null;
            initialFilters.owner = null;
            initialFilters.useSortConfig = false;
            initialFilters.dateFieldFilter = CTA.FILTERS.DATE_FIELD_FILTERS.LAST_END_USER_ACTIVITY;
            initialFilters.chatsId = [deepLinkChatId];
            initialFilters.status = null;
            initialFilters.statuses = null;
            setSearchInputVal(deepLinkChatId);
        }

        return initialFilters;
    };
    const { actions, appState } = useAppContext();
    const { t } = useTranslation();
    const filtersHeaderRef = useRef();
    const sectionRef = useRef();
    const headerRef = useRef();
    const [serverError, setServerError] = useState(false);
    const [filtersDomHeight, setFiltersDomHeight] = useState(0);
    const [showFilter, setShowFilter] = useState(CTA.FILTERS.ME);
    const [windowIsActive, setWindowIsActive] = useState(true);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [usersList] = useAllUsers();
    const [groupsList, setGroupsList] = useState(undefined);
    const [modalChat, setModalChat] = useState(null);
    const [modalView, setModalView] = useState(null);
    const [searchInputVal, setSearchInputVal] = useState("");
    const filterRef = useRef(showFilter);
    const selectedChatRef = useRef(appState.selectedChat);
    const agentDataRef = useRef(appState.agent);
    const [orderDirection, setOrderDirection] = useState(false);
    const windowIsActiveRef = useRef(windowIsActive);
    const [chatsPaginator, setChatsPaginator] = useState({ page: 0, hasNext: false });
    const [filtersCount, setFiltersCount] = useState(null);
    const [statusFilter, setStatusFilter] = useState(CTA.FILTERS.ACTIVE);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);

    const typingTimeoutRef = useRef(null);
    const [selectedTagGroups, setSelectedTagGroups] = useState([]);
    const [selectedTagGroup, setSelectedTagGroup] = useState(null);
    const [selectedGroupedConversationTags, setSelectedGroupedConversationTags] = useState({});
    const [datesError, setDatesError] = useState(false);
    const [searchOlderChats, setSearchOlderChats] = useState(false);

    const logger = new Logger("ChatList Agent", serverUrl);

    const useOnlyActiveInMeTab = useMemo(() => {
        const showAllChatsInMeTab = appState?.workspaceConfig?.agent?.showChatsOfAllStatus?.me;
        // Doing this so workspaces that does not have this config (old workspaces) keep the same
        // beahviour and we do not break agent workflow.
        // New workspaces have this config enabled by default
        return !showAllChatsInMeTab;
    }, [appState?.workspaceConfig?.agent?.showChatsOfAllStatus?.me]);

    const [filters, setFilters] = useState(() => setInitialFilters(false, useOnlyActiveInMeTab));
    const selectedChatId = useMemo(() => {
        return appState.selectedChat.id;
    }, [appState.selectedChat.id]);

    const currentUserId = useMemo(() => {
        return appState.agent.id;
    }, [appState.agent?.id]);

    const currentAgentGroups = useMemo(() => {
        return appState.agent.chatGroups;
    }, [appState.agent.chatGroups]);

    const conversationOrderType = useMemo(() => {
        return appState.workspaceConfig?.backend?.conversationOrderingType;
    }, [appState.workspaceConfig.backend.conversationOrderingType]);

    const disableNotifications = useMemo(() => {
        return Boolean(appState.workspaceConfig?.agent?.disableNotifications) || isMobileView();
    }, [appState.workspaceConfig?.agent?.disableNotifications]);

    const conversationLabels = useMemo(() => {
        return appState.conversationLabels;
    }, [appState.conversationLabels]);

    const conversationTagGroups = useMemo(() => {
        return appState.conversationTagGroups;
    }, [appState.conversationTagGroups]);

    const chatList = useMemo(() => {
        return appState.chats;
    }, [appState.chats]);

    const chatsAreLoading = useMemo(() => appState.chatsAreLoading, [appState.chatsAreLoading]);

    const [channels, setChannels] = useState({
        facebook: {
            isEnable: false,
        },
        webChat: {
            isEnable: false,
        },
        whatsapp: {
            isEnable: false,
        },
    });
    const navigate = useNavigate();

    /**
     * @type {number | undefined}
     */
    const deepLinkChatId = useMemo(() => extractChatIdFromUrl(window.location.href), []);
    const [deepLinkChatWasFetched, setDeepLinkChatWasFetched] = useState(false);

    async function loadChats() {
        actions.setChatsAreLoading(true);

        const newFilters = { ...filters };
        if (searchOlderChats) {
            const startDate = new Date(newFilters.startDatetime);
            const endDate = structuredClone(startDate);
            endDate.setDate(endDate.getDate() + 7);
            newFilters.endDatetime = endDate.toISOString();
        }

        try {
            const [chats, paginator] = await fetchChats(
                newFilters,
                useOnlyActiveInMeTab,
                searchOlderChats ? chatList : undefined
            );
            actions.setChats(chats);
            setChatsPaginator(paginator);
            setServerError(false);

            if (deepLinkChatId && !deepLinkChatWasFetched) {
                if (chats.find((c) => deepLinkChatId === c.id)) {
                    setStatusFilter(null);
                    setShowFilter(null);
                    openChatCb(chats.find((c) => c.id === deepLinkChatId));
                    console.log("seteando");
                    actions.setDeepLinkChatState(true, deepLinkChatId);
                } else {
                    actions.setDeepLinkChatState(false, deepLinkChatId);
                    navigate(CTA.VIEWS.CHAT_LIST);
                }
            }
            setDeepLinkChatWasFetched(true);
        } catch (err) {
            console.log(err);
            if (err.response?.status === 408 && err.response?.data?.error?.key === "TIMEOUT") {
                message.error(t("connection.timeoutConversations"));
            } else {
                setServerError(true);
                message.error(t("connection.errorLoadingConversations"));
            }
        } finally {
            actions.setChatsAreLoading(false);
        }
    }

    const handleLoadOlderChats = () => {
        setFilters((f) => ({
            ...f,
            startDatetime: addDaysToDatetime(f.startDatetime, -7),
        }));
    };
    const chats = useMemo(() => {
        const filterSortClauses = (conversations, chatFilters) => {
            return conversations
                ?.filter((chat) => filterChatByOwner(chat, appState.agent, showFilter))
                .filter((chat) => filterChatByStatus(chat, chatFilters))
                .filter((chat) => filterChatByWhichAreWaitingAnswer(chat, chatFilters))
                .filter((chat) => filterChatByChannel(chat, chatFilters))
                .sort((a, b) => sortByConfigTime(a, b, chatFilters, conversationOrderType));
        };

        if (filters.owner !== "ME" || useOnlyActiveInMeTab) {
            const chatsFiltered = filterSortClauses(chatList, filters);
            return chatsFiltered;
        }
        const activeChats = chatList.filter((c) => c.status === "ACTIVE");
        const otherChats = chatList.filter((c) => c.status !== "ACTIVE");
        return [
            ...filterSortClauses(activeChats, { ...filters, useSortConfig: true }),
            ...filterSortClauses(otherChats, filters),
        ];
    }, [chatList, appState.agent, showFilter, filters, conversationOrderType]);

    /**
     *
     * @param {String} participant
     * @returns {void}
     */
    function handleParticipantChange(participant) {
        const chatFilterStatus = filters.status;
        setShowFilter(participant);
        const newFilters = {
            owner: CTA.FILTERS.ME,
            useSortConfig: true,
            sortBy: null,
            status: "ACTIVE",
        };
        if (participant === CTA.FILTERS.ME && !useOnlyActiveInMeTab) {
            newFilters.useSortConfig = false;
            newFilters.status = "ALL";
            newFilters.sortBy = CTA.FILTERS.LAST_END_USER_ACTIVITY;
        } else if (participant === CTA.FILTERS.AI_AGENTS) {
            newFilters.participatingAgentsId = null;
            newFilters.onlyOwners = null;
            newFilters.owner = CTA.FILTERS.AI_AGENTS;
            newFilters.useSortConfig = false;
            newFilters.sortBy = CTA.FILTERS.LAST_END_USER_ACTIVITY;
            newFilters.statuses = undefined;
        } else if (participant === CTA.FILTERS.ALL) {
            newFilters.owner = CTA.FILTERS.HUMAN_AGENTS;
            newFilters.statuses = undefined;
        } else if (participant === CTA.FILTERS.UNASSIGNED) {
            newFilters.owner = CTA.FILTERS.UNASSIGNED;
            newFilters.statuses = undefined;
        } else if (chatFilterStatus === CTA.FILTERS.ENDED) {
            newFilters.useSortConfig = false;
            newFilters.sortBy = CTA.FILTERS.LAST_END_USER_ACTIVITY;
            newFilters.statuses = undefined;
        }
        setFilters((state) => ({ ...state, ...newFilters }));
    }

    function handleWaitingFilter(val) {
        const newVal = filters.isWaiting ? null : true;
        const newFilters = { ...filters };
        newFilters.isWaiting = newVal;
        setFilters(newFilters);
    }

    function handleOnlyOwnerFilter(val) {
        const newFilters = { ...filters };
        newFilters.onlyOwners = val;
        setFilters(newFilters);
    }

    function handleChatGroupsFilter(selected) {
        const newFilters = { ...filters };
        if (selected.length > 0) {
            newFilters.filterByUserGroups = false;
            newFilters.chatGroupsId = selected;
            newFilters.onlyChatsInGroups = true;
        } else {
            newFilters.filterByUserGroups = null;
            newFilters.chatGroupsId = null;
            delete newFilters.onlyChatsInGroups;
        }
        setFilters(newFilters);
    }

    function handleRangeDateChange(date, dateString) {
        if (date && date[0] && date[1]) {
            const diff = date[1].diff(date[0], "days");
            const isError = diff > 32;
            setDatesError(diff > 32);
            if (isError) {
                return;
            }
        }

        const newFilters = { ...filters };
        newFilters.startDatetime = date ? moment(dateString[0]).format() : null;
        newFilters.endDatetime = date ? moment(dateString[1]).format() : null;
        setFilters(newFilters);
    }
    /**
     *
     * @param {boolean | null} val
     */
    function handlenotServedFilter(val) {
        const newFilters = { ...filters };
        newFilters.notServed = val;
        setFilters(newFilters);
    }

    /**
     *
     * @param {Array<number>} selected Selected label ids
     */
    function handleConversationLabelFilter(selected) {
        const newFilters = { ...filters };
        newFilters.conversationLabels = selected.length === 0 ? null : selected;
        setFilters(newFilters);
    }

    function handleConversationAgentsFilter(selected) {
        const newFilters = { ...filters };
        newFilters.participatingAgentsId = selected.length === 0 ? null : selected;
        if (selected.length > 0 && filters.onlyOwners === null) {
            newFilters.onlyOwners = true;
        } else if (selected.length === 0) {
            newFilters.onlyOwners = null;
        }
        setFilters(newFilters);
    }

    function handleChannelFilterChange(channel) {
        const newFilters = { ...filters };
        newFilters.customerChannel = channel;
        setFilters(newFilters);
    }

    function handleStatusChange(val) {
        const newFilters = { ...filters };
        if (val === CTA.FILTERS.ENDED) {
            newFilters.status = val;
            newFilters.useSortConfig = false;
            newFilters.sortBy = CTA.FILTERS.LAST_END_USER_ACTIVITY;

            const startDatetime = moment().subtract(3, "d");
            startDatetime.set({ hour: 0, minute: 0, seconds: 0 });
            newFilters.startDatetime = startDatetime.format();

            const endDateTime = moment();
            endDateTime.set({ hour: 23, minute: 59, seconds: 59 });
            newFilters.endDatetime = endDateTime.format();

            newFilters.dateFieldFilter = CTA.FILTERS.DATE_FIELD_FILTERS.CLOSED_ON;
        } else if (val === CTA.FILTERS.ACTIVE) {
            newFilters.status = val;
            newFilters.useSortConfig = true;
            newFilters.sortBy = null;
            newFilters.startDatetime = null;
            newFilters.endDatetime = null;
            newFilters.dateFieldFilter = null;
        } else if (val === CTA.FILTERS.INIT_BY_AGENT) {
            newFilters.status = CTA.FILTERS.INIT_BY_AGENT;
            newFilters.useSortConfig = false;
            newFilters.sortBy = CTA.FILTERS.LAST_END_USER_ACTIVITY;
            newFilters.startDatetime = null;
            newFilters.endDatetime = null;
            newFilters.dateFieldFilter = null;
        }
        setFilters(newFilters);
        setStatusFilter(val);
    }

    async function changePage() {
        actions.setChatsAreLoading(true);
        try {
            const res = await api
                .chats()
                .list({ ...filters, page: chatsPaginator.page + 1, amount: 8 });
            actions.setChats([...appState.chats, ...res.data.conversations]);
            setChatsPaginator((p) => ({
                page: p.page + 1,
                hasNext: res.data.paginator.hasNext,
            }));
        } catch (err) {
            message.error(t("connection.errorLoadingConversations"));
            console.log(err);
        } finally {
            actions.setChatsAreLoading(false);
        }
    }

    async function handleChatOptions(chat, owner, action) {
        const loaderHash = actions.setLoader();
        try {
            // Assign || Unassign
            if (action === CTA.CHATS.OPTIONS.ASSIGN || action === CTA.CHATS.OPTIONS.UNASSIGN) {
                await api.chats().updateChat(chat.id, { markAsOwnedBy: owner });
                actions.setChatOwner(chat, owner);
                if (action === CTA.CHATS.OPTIONS.ASSIGN && chat.id === appState.selectedChat?.id) {
                    const selected = {
                        ...chat,
                        ownedBy: {
                            ...appState.agent,
                            isCurrentUser: true,
                        },
                    };
                    actions.setSelectedChat(selected);
                    actions.setCurrentChatOption(selected.id, CTA.CHATS.OPTIONS.RELOAD);
                }
                if (
                    action === CTA.CHATS.OPTIONS.UNASSIGN &&
                    chat.id === appState.selectedChat?.id
                ) {
                    actions.setSelectedChat({});
                }
                actions.unsetLoader(loaderHash);
            } else if (action === CTA.CHATS.OPTIONS.CLOSE) {
                await api.chats().close(chat.id);
                actions.setClosedChat(chat);
                actions.addOrUpdateChatAction({ ...chat, status: "ENDED" });
                if (chat.id === appState.selectedChat?.id) {
                    actions.setSelectedChat({});
                }
            } else if (action === CTA.CHATS.OPTIONS.MARK_READ) {
                api.chats().updateChat(chat.id, { markRead: true });
                actions.markChatRead(chat);
            } else if (action === CTA.CHATS.OPTIONS.MARK_UNREAD) {
                api.chats().updateChat(chat.id, { markUnread: true });
                actions.markChatUnread(chat);
            } else {
                throw Error(`Action ${action} handler is not implemented`);
            }
        } catch (err) {
            console.log(err);
        } finally {
            actions.unsetLoader(loaderHash);
        }
    }

    function checkNewNotificationsInCurrentList() {
        const chatsWaiting = appState.chats.filter(
            (item) => item.ownedBy?.id === appState.agent.id && item.startedWaitingOn
        );
        const anyChatsWaiting = chatsWaiting.length > 0;
        if (anyChatsWaiting) {
            actions.setNewChatNotification(true);
        } else {
            actions.setNewChatNotification(false);
            document.title = "Optiwe Agent";
        }
    }

    function getPushLocalConfig() {
        const pushLocalConfig = JSON.parse(
            localStorage.getItem(CTA.LOCAL_STORAGE_KEYS.FCM_NOTIFICATIONS)
        );
        return pushLocalConfig.show;
    }

    /**
     *
     * @param {import("frontend-lib/DomainTypes/Conversation").Conversation} chat
     * @param {boolean} showNewMessage
     */
    function checkNewNotificationOnChat(chat, showNewMessage) {
        //@ts-check
        if (chat.ownedBy?.id === appState.agent.id && chat.amountNew > 0) {
            actions.setNewChatNotification(true);
            let showNotification = false;
            if (!windowIsActiveRef.current && showNewMessage) {
                showNotification = true;
            } else if (chat.id !== selectedChatRef.current.id && showNewMessage) {
                showNotification = true;
            }

            if (showNotification) {
                if (chat.status === "INIT_BY_AGENT") {
                    // logger.error(
                    //     `Wanted to play notification sound in checkNewNotificationOnChat but conversation ${
                    //         chat.id
                    //     } is in INIT_BY_AGENT state.
                    //     Full conversation is ${JSON.stringify(chat)}`
                    // );
                } else {
                    notiAudio.play();
                }

                document.title = "(+1) Optiwe Agent";

                const pushNotificationsAreEnabled = getPushLocalConfig();
                if (!pushNotificationsAreEnabled && !disableNotifications) {
                    const messageFrom = chat.customer.fullName
                        ? chat.customer.fullName
                        : t("customerUnidentified");
                    const message = t("newMessageFrom", { customer: messageFrom });
                    notification["info"]({
                        key: "update",
                        message: t("newMessages"),
                        description: message,
                        duration: 0,
                    });
                }
            }
        }
    }

    function handleConversationUpdated(msg) {
        const utf8decoder = new TextDecoder();
        const jsonMsg = JSON.parse(utf8decoder.decode(msg));
        checkNewNotificationOnChat(jsonMsg.conversation, true);
        actions.addOrUpdateChatAction(jsonMsg.conversation);
    }

    function handleConversationMetadataUpdated(msg) {
        const utf8decoder = new TextDecoder();
        const jsonMsg = JSON.parse(utf8decoder.decode(msg));
        const prevSelectedChat = { ...selectedChatRef.current };
        checkNewNotificationOnChat(jsonMsg.conversation, false);
        actions.updateChat(jsonMsg.conversation);

        /**
         * @type {import("frontend-lib/DomainTypes/Conversation").Conversation | undefined}
         */
        const conversation = jsonMsg.conversation;

        /*
         * Si el nuevo mensaje es del chat seleccionado
         * Si el nuevo mensaje tiene como owner al agente logeado
         * Si el chat seleccionado tenía otro owned antes de que llegue
         */

        if (
            conversation?.id === selectedChatRef.current?.id &&
            conversation.ownedBy?.id === agentDataRef.current?.id &&
            prevSelectedChat.ownedBy?.id !== conversation?.ownedBy?.id
        ) {
            const selected = {
                ...jsonMsg.conversation,
                ownedBy: {
                    ...agentDataRef.current,
                    isCurrentUser: true,
                },
            };
            actions.setSelectedChat(selected);
            // Si ya tenía un owner es porque fue derivado
            if (prevSelectedChat.ownedBy) {
                actions.setCurrentChatOption(conversation.id, CTA.CHATS.OPTIONS.RELOAD);
            }
            // Si no tenía owner quiere decir que se lo asignó
        }

        if (
            // If conversation was assigned to ME and is not in current chat list, add the new conv to chatlist
            conversation &&
            conversation.ownedBy?.id === agentDataRef.current?.id &&
            filters.owner === CTA.FILTERS.ME &&
            !chatList.find((c) => c.id === conversation.id)
        ) {
            actions.addOrUpdateChatAction(conversation);
        }
    }

    function handleConversationCreated(msg) {
        const prevSelectedChat = { ...selectedChatRef.current };
        const utf8decoder = new TextDecoder();
        const jsonMsg = JSON.parse(utf8decoder.decode(msg));

        /**
         * @type {import("frontend-lib/DomainTypes/Conversation").Conversation}
         */
        const newConversation = jsonMsg.conversation;

        if (
            filterRef.current === CTA.CHATS.OPTIONS.UNASSIGNED ||
            filterRef.current === CTA.CHATS.OPTIONS.ALL ||
            filterRef.current === CTA.CHATS.OPTIONS.ME
        ) {
            actions.addOrUpdateChatAction(newConversation);
            const pushNotificationsAreEnabled = getPushLocalConfig();

            document.title = "(+1) Optiwe Agent";

            if (newConversation.status === "INIT_BY_AGENT") {
                // logger.error(
                //     `Wanted to play notification sound in handleConversationCreated but conversation ${
                //         newConversation.id
                //     } is in INIT_BY_AGENT state.
                //     Full conversation is ${JSON.stringify(newConversation)}`
                // );
            } else {
                notiAudio.play();
            }

            if (!pushNotificationsAreEnabled && !disableNotifications) {
                const messageFrom = newConversation.customer.fullName
                    ? newConversation.customer.fullName
                    : t("customerUnidentified");
                const message = t("newConversationFrom", { customer: messageFrom });
                notification["success"]({
                    key: "newChat",
                    message: t("newChat"),
                    description: message,
                    duration: 0,
                });
            }
        }

        if (prevSelectedChat.id === newConversation.id) {
            actions.setSelectedChat(newConversation);
            actions.setCurrentChatOption(newConversation.id, CTA.CHATS.OPTIONS.RELOAD);
        }
    }

    function handleSearchInput(text) {
        setSearchInputVal(text);

        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
            if (text) {
                const startDatetime = moment().subtract(3, "d");
                startDatetime.set({ hour: 0, minute: 0, seconds: 0 });

                const endDateTime = moment();
                endDateTime.set({ hour: 23, minute: 59, seconds: 59 });

                setFilters((currState) => ({
                    ...currState,
                    searchText: text,
                    status: null,
                    owner: null,
                    useSortConfig: false,
                    sortBy: CTA.FILTERS.LAST_END_USER_ACTIVITY,
                    startDatetime: startDatetime.format(),
                    endDatetime: endDateTime.format(),
                    dateFieldFilter: CTA.FILTERS.DATE_FIELD_FILTERS.LAST_END_USER_ACTIVITY,
                }));
                setStatusFilter(null);
                setShowFilter(null);
                setSearchOlderChats(true);
            } else {
                setFilters((currState) => {
                    const newState = {
                        ...currState,
                        searchText: null,
                        status: CTA.FILTERS.ACTIVE,
                        owner: CTA.FILTERS.ME,
                        useSortConfig: true,
                        sortBy: null,
                        startDatetime: null,
                        endDatetime: null,
                        dateFieldFilter: null,
                        chatsId: undefined,
                    };
                    if (useOnlyActiveInMeTab) return newState;
                    return {
                        ...newState,
                        useSortConfig: false,
                        status: "ALL",
                        sortBy: CTA.FILTERS.LAST_END_USER_ACTIVITY,
                    };
                });
                setStatusFilter(CTA.FILTERS.ACTIVE);
                setShowFilter(CTA.FILTERS.ME);
                setSearchOlderChats(false);
            }
        }, [500]);
    }

    async function handleModal(option, selectedChat) {
        try {
            setEditModalVisible(true);
            setModalChat(selectedChat);
            if (option === CTA.CHATS.OPTIONS.DERIVATE) {
                setModalView(CTA.CHATS.OPTIONS.DERIVATE);
            }
            if (option === CTA.CHATS.OPTIONS.CHANGE_GROUP) {
                setModalView(CTA.CHATS.OPTIONS.CHANGE_GROUP);
            }
        } catch (err) {
            console.log(err);
        }
    }

    function handleCloseModal() {
        setEditModalVisible(false);
        setModalChat(null);
        setModalView(null);
    }

    // User has switched back to the tab
    const onFocus = () => {
        setWindowIsActive(true);
    };

    // User has switched away from the tab (AKA tab is hidden)
    const onBlur = () => {
        setWindowIsActive(false);
    };

    function getFormattedTime(chat) {
        let effectiveSortBy;
        if (filters.sortBy) {
            effectiveSortBy = filters.sortBy;
        } else {
            effectiveSortBy = conversationOrderType;
        }

        if (effectiveSortBy === "LAST_END_USER_ACTIVITY") {
            return moment(chat.lastEndUserActivity).fromNow().replace("ago", "");
        } else if (
            effectiveSortBy === "START_WAITING_ON" ||
            effectiveSortBy === "ASSIGNED_TO_HUMAN_AGENT_ON"
        ) {
            if (chat && chat.startedWaitingOn) {
                return moment(chat.startedWaitingOn).fromNow().replace("ago", "");
            } else {
                return "";
            }
        } else {
            return "";
        }
    }

    function getChatDescription(chat) {
        const formattedTime = getFormattedTime(chat);
        let chatDescription;
        if (formattedTime) {
            chatDescription = formattedTime + " - ID: " + String(chat.id);
        } else {
            chatDescription = "ID: " + String(chat.id);
        }
        return chatDescription;
    }

    function checkFiltersApplied() {
        const validFilters = [
            "isWaiting",
            "filterByUserGroups",
            "conversationLabels",
            "orderDirection",
            "participatingAgentsId",
            "startDatetime",
            "groupedConversationTags",
            "customerChannel",
            "sortBy",
            "status",
            "onlyOwners",
            "notServed",
        ];
        const currFilters = Object.entries(filters);
        let count = 0;
        currFilters.forEach((filter) => {
            if (validFilters.includes(filter[0]) && filter[1] !== null && filter[1] !== undefined) {
                count++;
            }
        });
        setFiltersCount(count);
    }

    function loadTags() {
        api.tags()
            .list()
            .then((res) => {
                actions.setConversationLabels(res.data.conversationLabels);
            })
            .catch((err) => {
                console.log(err);
            });
    }

    function loadGroups() {
        api.tagsGroups()
            .list()
            .then((res) => {
                actions.setConversationTagGroups(res.data.conversationLabelGroups);
            });
    }

    function showMoreFiltersClick() {
        setIsDropdownVisible(!isDropdownVisible);
        checkFiltersApplied();
    }

    useEffect(() => {
        loadChats();
        checkFiltersApplied();
        actions.setChatsListFilters(filters);
        // eslint-disable-next-line
    }, [filters]);

    useEffect(() => {
        if (notificationSocket) {
            notificationSocket.on("conversation_updated", handleConversationUpdated);
            notificationSocket.on(
                "conversation_metadata_updated",
                handleConversationMetadataUpdated
            );
            notificationSocket.on("conversation_created", handleConversationCreated);
        }
        // eslint-disable-next-line
    }, [notificationSocket]);

    useEffect(() => {
        filterRef.current = showFilter;
        selectedChatRef.current = appState.selectedChat;
        windowIsActiveRef.current = windowIsActive;
    }, [showFilter, selectedChatId, windowIsActive]);

    useEffect(() => {
        agentDataRef.current = appState.agent;
    }, [currentUserId]);

    useEffect(() => {
        if (showFilter === CTA.FILTERS.ME || showFilter === CTA.FILTERS.ALL) {
            checkNewNotificationsInCurrentList();
        }
        // eslint-disable-next-line
    }, [appState.chats]);

    useEffect(() => {
        if (appState.chatOwnerButton) {
            actions.setChatOwnerButton(false);
            if (selectedChatId === appState.selectedChat.id) {
                handleChatOptions(
                    appState.selectedChat,
                    CTA.CHATS.OPTIONS.ME,
                    CTA.CHATS.OPTIONS.ASSIGN
                );
            } else {
                actions.setLoader();
                notification["error"]({
                    message: "Error",
                    description: t("chatSocketError"),
                    duration: 0,
                });
            }
        }
        // eslint-disable-next-line
    }, [appState.chatOwnerButton]);

    useEffect(() => {
        const newFilters = { ...filters };
        if (
            !filters.sortBy ||
            filters.sortBy === "LAST_END_USER_ACTIVITY" ||
            filters.status === CTA.FILTERS.ENDED
        ) {
            if (orderDirection) {
                newFilters.orderDirection = CTA.FILTERS.ASC;
            } else {
                newFilters.orderDirection = null;
            }
        } else if (filters.sortBy === "START_WAITING_ON") {
            if (orderDirection) {
                newFilters.orderDirection = CTA.FILTERS.DESC;
            } else {
                newFilters.orderDirection = null;
            }
        }
        setFilters(newFilters);
    }, [orderDirection]);

    useEffect(() => {
        api.auth()
            .channels()
            .then((res) => {
                const channels = res.data.channels;
                setChannels(channels);
            });

        api.chats()
            .groups()
            .then((res) => {
                setGroupsList(res.data.chatGroups);
            });

        loadGroups();

        window.addEventListener("focus", onFocus);
        window.addEventListener("blur", onBlur);

        return () => {
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("blur", onBlur);
        };
    }, []);

    useEffect(() => {
        setFiltersDomHeight(filtersHeaderRef?.current?.clientHeight);
    }, [isDropdownVisible, filters]);

    async function handleSelectConversationTagGroup(selectedTagGroup) {
        if (!selectedTagGroup) {
            return;
        }
        const groupName = selectedTagGroup.value;
        try {
            const group = conversationTagGroups.find((g) => g.name === groupName);
            const res = await api.tagsGroups().get(group.id);
            const newGroup = res.data;
            const newSelectedGroups = [...selectedTagGroups, newGroup];
            setSelectedTagGroups(newSelectedGroups);
            setSelectedTagGroup(null);
        } catch (err) {
            console.log(err);
        }
    }

    function buildGroupedTags(selectedConversationTagGroups) {
        let groupedTags = null;
        // eslint-disable-next-line
        for (const [_, ids] of Object.entries(selectedConversationTagGroups)) {
            if (ids && ids.length) {
                if (!groupedTags) {
                    groupedTags = [];
                }
                groupedTags.push(ids);
            }
        }
        return groupedTags;
    }

    function handleDeselectConversationTagGroup(value) {
        const groupId = parseInt(value.key);
        const groupToDeselect = selectedTagGroups.find((g) => g.id === groupId);
        const newSelectedGroupConversationTags = { ...selectedGroupedConversationTags };
        delete newSelectedGroupConversationTags[groupToDeselect.name];
        const newSelectedGroups = selectedTagGroups.filter((g) => g.id !== groupId);
        const groupedTags = buildGroupedTags(newSelectedGroupConversationTags);
        setSelectedTagGroups(newSelectedGroups);
        setSelectedGroupedConversationTags(newSelectedGroupConversationTags);
        handleConversationGroupFilter(groupedTags);
    }

    function handleChangeConversationLabel(group, value) {
        const newGroupedTags = { ...selectedGroupedConversationTags };
        newGroupedTags[group.name] = value.map((v) => parseInt(v.key));
        setSelectedGroupedConversationTags(newGroupedTags);
        const groupedTags = buildGroupedTags(newGroupedTags);
        handleConversationGroupFilter(groupedTags);
    }

    function handleConversationGroupFilter(groupedTags) {
        const newFilters = { ...filters };
        newFilters.groupedConversationTags = groupedTags;
        setFilters(newFilters);
    }

    function onSelectSortByButton(selectedValue) {
        const actualSortBy = filters.sortBy;

        if (selectedValue === actualSortBy) return;

        let newSortBy = selectedValue;
        let newUseSortConfig;
        if (!selectedValue) {
            newUseSortConfig = true;
        } else {
            newUseSortConfig = false;
        }

        setFilters((actualFilters) => {
            return { ...actualFilters, sortBy: newSortBy, useSortConfig: newUseSortConfig };
        });
    }

    function renderSortByButton() {
        if (
            statusFilter === CTA.FILTERS.ENDED ||
            filters.owner === CTA.FILTERS.AI_AGENTS ||
            filters.status === CTA.FILTERS.INIT_BY_AGENT
        ) {
            return <></>;
        }
        return (
            <>
                <Select
                    suffixIcon={<FieldTimeOutlined />}
                    onSelect={onSelectSortByButton}
                    value={{
                        value: filters.sortBy,
                        label: filters.sortBy
                            ? t(`filters.${filters.sortBy}`)
                            : t(`filters.default`),
                    }}
                    options={[
                        { value: null, label: t("filters.default") },
                        {
                            value: CTA.FILTERS.DATE_FIELD_FILTERS.START_WAITING_ON,
                            label: t("filters.START_WAITING_ON"),
                        },
                        {
                            value: CTA.FILTERS.DATE_FIELD_FILTERS.LAST_END_USER_ACTIVITY,
                            label: t("filters.LAST_END_USER_ACTIVITY"),
                        },
                        {
                            value: CTA.FILTERS.DATE_FIELD_FILTERS.ASSIGNED_TO_HUMAN_AGENT_ON,
                            label: t("filters.ASSIGNED_TO_HUMAN_AGENT_ON"),
                        },
                    ]}
                />
            </>
        );
    }
    const isDerivateModalVisible = useMemo(
        () => modalView === CTA.CHATS.OPTIONS.DERIVATE && editModalVisible,
        [modalView, editModalVisible]
    );

    const isSelectGroupModalVisible = useMemo(
        () => !isDerivateModalVisible && editModalVisible,
        [isDerivateModalVisible, editModalVisible]
    );

    return (
        <section
            ref={sectionRef}
            className={`${className} chat-list`}
            style={{ zIndex: active ? 1 : 0 }}
        >
            <DerivateModal
                visible={isDerivateModalVisible}
                selectedChat={modalChat}
                handleCloseModal={handleCloseModal}
            />
            <SelectGroupModal
                visible={isSelectGroupModalVisible}
                selectedChat={modalChat}
                loadChatsCb={loadChats}
                handleCloseModalCb={handleCloseModal}
                groups={groupsList}
            />
            <div style={{ height: "100%" }}>
                <ChatListHeaders
                    headerRef={headerRef}
                    disableNotifications={disableNotifications}
                    channels={channels}
                    logoutCb={logoutCb}
                    usersList={usersList}
                />
                <div className={chatListView}>
                    <div ref={filtersHeaderRef}>
                        <ChatListFilterHeaders
                            showFilter={showFilter}
                            searchInputVal={searchInputVal}
                            filtersCount={filtersCount}
                            handleStatusChangeCb={handleStatusChange}
                            handleParticipantChangeCb={handleParticipantChange}
                            handleSearchInputCb={handleSearchInput}
                            showMoreFiltersClickCb={showMoreFiltersClick}
                            filters={filters}
                            handlers={{
                                handleChangeChatGroup: handleChatGroupsFilter,
                                handleChangeChannel: handleChannelFilterChange,
                                handleWaitingFilter: handleWaitingFilter,
                                handleChangeSortBy: onSelectSortByButton,
                                handleSelectAgents: handleConversationAgentsFilter,
                                handleSelectConversationLabels: handleConversationLabelFilter,
                                handleDateRangeChange: handleRangeDateChange,
                                handleStatusChangeCb: handleStatusChange,
                                handleNotServed: handlenotServedFilter,
                            }}
                            chatListState={{
                                orderDirection: orderDirection,
                                setOrderDirection: setOrderDirection,
                                usersList: usersList,
                            }}
                        />
                        <div className={filtersDropdown}>
                            <div
                                className={
                                    isDropdownVisible
                                        ? filtersMainContainer__visible
                                        : filtersMainContainer
                                }
                            >
                                <div
                                    className={
                                        isDropdownVisible
                                            ? filtersInnerContainer__visible
                                            : filtersInnerContainer
                                    }
                                >
                                    <div style={{ margin: "0 13px 8px 13px" }}>
                                        <div>
                                            <Select
                                                style={{ width: "100%" }}
                                                value={statusFilter}
                                                onChange={handleStatusChange}
                                                placeholder={t("filters.selectAState")}
                                            >
                                                <Option value={CTA.FILTERS.ACTIVE}>
                                                    {t("filters.active")}
                                                </Option>
                                                <Option value={CTA.FILTERS.INIT_BY_AGENT}>
                                                    {t("filters.init_by_agent")}
                                                </Option>
                                                <Option value={CTA.FILTERS.ENDED}>
                                                    {t("filters.closed")}
                                                </Option>
                                            </Select>
                                        </div>
                                    </div>
                                    {filters.searchText && (
                                        <div style={{ margin: "0 13px 8px 13px" }}>
                                            <div>
                                                <Select
                                                    allowClear={true}
                                                    style={{ width: "100%" }}
                                                    value={showFilter}
                                                    onChange={(newValue) =>
                                                        handleParticipantChange({
                                                            target: { value: newValue },
                                                        })
                                                    }
                                                >
                                                    <Option value={CTA.FILTERS.ME}>
                                                        {t("filters.me")}
                                                    </Option>
                                                    <Option value={CTA.FILTERS.UNASSIGNED}>
                                                        {t("filters.unassigned")}
                                                    </Option>
                                                    <Option value={CTA.FILTERS.AI_AGENTS}>
                                                        {t("filters.bot")}
                                                    </Option>
                                                </Select>
                                            </div>
                                        </div>
                                    )}
                                    {(filters.status === "ENDED" || filters.searchText) && (
                                        <>
                                            <div style={{ margin: "0 13px 8px 13px" }}>
                                                <div>
                                                    <Tooltip
                                                        title={
                                                            datesError
                                                                ? t("filters.datesError")
                                                                : ""
                                                        }
                                                        visible={datesError}
                                                    >
                                                        <RangePicker
                                                            inputReadOnly
                                                            onChange={handleRangeDateChange}
                                                            allowClear={false}
                                                            style={{ width: "100%" }}
                                                            value={[
                                                                moment(filters.startDatetime),
                                                                moment(filters.endDatetime),
                                                            ]}
                                                            showTime={{ format: "HH:mm" }}
                                                            format="YYYY-MM-DD HH:mm"
                                                        />
                                                    </Tooltip>
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    margin: "0 13px 8px 13px",
                                                    justifyContent: "space-between",
                                                    display: "flex",
                                                }}
                                            >
                                                <div
                                                    className="filter-was-not-served"
                                                    onClick={() =>
                                                        handlenotServedFilter(
                                                            filters.notServed ? null : true
                                                        )
                                                    }
                                                >
                                                    <span className="filter-was-not-served-text">
                                                        {t("filters.wasNotServed")}
                                                    </span>
                                                    <Switch
                                                        checked={filters.notServed}
                                                        size="small"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {groupsList && groupsList.length > 0 && (
                                        <div style={{ margin: "0 13px 8px 13px" }}>
                                            <div>
                                                <Select
                                                    mode="multiple"
                                                    defaultActiveFirstOption={false}
                                                    onChange={handleChatGroupsFilter}
                                                    style={{ width: "100%" }}
                                                    allowClear={true}
                                                    placeholder={t("filters.selectGroups")}
                                                    filterOption={(input, option) =>
                                                        option.props.children
                                                            .toLowerCase()
                                                            .indexOf(input.toLowerCase()) >= 0
                                                    }
                                                >
                                                    {currentAgentGroups?.map((group) => (
                                                        <Option
                                                            value={group.id}
                                                            key={`currentAgentAGroup:${group.id}`}
                                                        >
                                                            {group.name}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </div>
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            margin: "0 13px 8px 13px",
                                            justifyContent: "space-between",
                                            display: "flex",
                                        }}
                                    >
                                        <Select
                                            mode="multiple"
                                            defaultActiveFirstOption={false}
                                            onChange={handleConversationAgentsFilter}
                                            style={{ width: "100%" }}
                                            placeholder={t("filters.selectAgents")}
                                            allowClear={true}
                                            showSearch={true}
                                            filterOption={(input, option) =>
                                                option.props.children
                                                    .join()
                                                    .toLowerCase()
                                                    .indexOf(input.toLowerCase()) >= 0
                                            }
                                            dropdownRender={(menu) => <>{menu}</>}
                                        >
                                            {usersList.map((user) => (
                                                <Option key={user.id} value={user.id}>
                                                    {user.firstName} {user.lastName}: {user.email}
                                                </Option>
                                            ))}
                                        </Select>
                                    </div>
                                    <div
                                        style={{
                                            margin: "0 13px 8px 13px",
                                            justifyContent: "space-between",
                                            display: "flex",
                                        }}
                                    >
                                        <div
                                            className="filter-only-by-chat-owner"
                                            onClick={() =>
                                                handleOnlyOwnerFilter(
                                                    filters.onlyOwners ? false : true
                                                )
                                            }
                                        >
                                            <span className="filter-only-by-chat-owner-text">
                                                {t("filters.onlyOwner")}
                                            </span>
                                            <Switch checked={filters.onlyOwners} size="small" />
                                        </div>
                                    </div>
                                    <div style={{ margin: "0 13px 8px 13px" }}>
                                        <div>
                                            <Select
                                                defaultActiveFirstOption={false}
                                                onChange={handleChannelFilterChange}
                                                style={{ width: "100%" }}
                                                placeholder={t("filters.selectAChannel")}
                                                allowClear={true}
                                            >
                                                <Option
                                                    key={CTA.CHANNELS.WHATSAPP}
                                                    value={CTA.CHANNELS.WHATSAPP}
                                                >
                                                    {t("whatsapp")}
                                                </Option>
                                                <Option
                                                    key={CTA.CHANNELS.INSTAGRAM}
                                                    value={CTA.CHANNELS.INSTAGRAM}
                                                >
                                                    {t("instagram")}
                                                </Option>
                                                <Option
                                                    key={CTA.CHANNELS.FACEBOOK}
                                                    value={CTA.CHANNELS.FACEBOOK}
                                                >
                                                    {t("facebook")}
                                                </Option>
                                                <Option
                                                    key={CTA.CHANNELS.FACEBOOK_COMMENT}
                                                    value={CTA.CHANNELS.FACEBOOK_COMMENT}
                                                >
                                                    {t("facebookComment")}
                                                </Option>
                                                <Option
                                                    key={CTA.CHANNELS.OPTIWE_CHAT}
                                                    value={CTA.CHANNELS.OPTIWE_CHAT}
                                                >
                                                    {t("webChat")}
                                                </Option>
                                                <Option
                                                    key={CTA.CHANNELS.SMS}
                                                    value={CTA.CHANNELS.SMS}
                                                >
                                                    {t("sms")}
                                                </Option>
                                            </Select>
                                        </div>
                                    </div>
                                    {conversationTagGroups && conversationTagGroups.length ? (
                                        <div
                                            className={filterConversationTag}
                                            style={{ overflow: "auto", maxHeight: "195px" }}
                                        >
                                            <div style={{ marginBottom: "8px", display: "flex" }}>
                                                <Select
                                                    labelInValue
                                                    style={{ width: "72%" }}
                                                    value={selectedTagGroup ? selectedTagGroup : []}
                                                    placeholder={t(
                                                        "filters.selectAGroupPlaceholder"
                                                    )}
                                                    onSelect={setSelectedTagGroup}
                                                    showSearch={true}
                                                    filterOption={(input, option) =>
                                                        option.props.children
                                                            .toLowerCase()
                                                            .indexOf(input.toLowerCase()) >= 0
                                                    }
                                                >
                                                    {conversationTagGroups
                                                        .filter(
                                                            (group) =>
                                                                !selectedTagGroups
                                                                    .map((g) => g.id)
                                                                    .includes(group.id)
                                                        )
                                                        .map((g) => (
                                                            <Option key={g.id} value={g.name}>
                                                                {g.name}
                                                            </Option>
                                                        ))}
                                                </Select>
                                                <Button
                                                    onClick={() =>
                                                        handleSelectConversationTagGroup(
                                                            selectedTagGroup
                                                        )
                                                    }
                                                    style={{ marginLeft: "auto" }}
                                                >
                                                    {t("filters.addGroup")}
                                                </Button>
                                            </div>
                                            {selectedTagGroups && selectedTagGroups.length
                                                ? selectedTagGroups.map((g) => (
                                                      <div
                                                          key={`group:${g.id}`}
                                                          style={{
                                                              borderLeft: "2px solid #b5afba",
                                                              paddingLeft: "8px",
                                                          }}
                                                      >
                                                          <div style={{ display: "flex" }}>
                                                              <label>
                                                                  {t("filters.filterBy") + g.name}
                                                              </label>
                                                              <button
                                                                  onClick={() => {
                                                                      handleDeselectConversationTagGroup(
                                                                          { key: g.id }
                                                                      );
                                                                  }}
                                                                  style={{
                                                                      marginLeft: "auto",
                                                                      marginRight: 0,
                                                                      background: "none",
                                                                      border: "none",
                                                                      color: "red",
                                                                      cursor: "pointer",
                                                                  }}
                                                              >
                                                                  {t("filters.remove")}
                                                              </button>
                                                          </div>
                                                          <div
                                                              key={`group:${g.id}`}
                                                              style={{
                                                                  marginTop: "2px",
                                                                  marginBottom: "8px",
                                                              }}
                                                          >
                                                              <Select
                                                                  mode="multiple"
                                                                  labelInValue
                                                                  style={{ width: "100%" }}
                                                                  placeholder={
                                                                      t(
                                                                          "filters.selectTagsForPlaceholder"
                                                                      ) + g.name
                                                                  }
                                                                  onChange={(value) =>
                                                                      handleChangeConversationLabel(
                                                                          g,
                                                                          value
                                                                      )
                                                                  }
                                                                  showSearch={true}
                                                                  filterOption={(input, option) =>
                                                                      option.props.children
                                                                          .toLowerCase()
                                                                          .indexOf(
                                                                              input.toLowerCase()
                                                                          ) >= 0
                                                                  }
                                                              >
                                                                  {g.conversationLabels.map((t) => (
                                                                      <Option key={t.id}>
                                                                          {t.value}
                                                                      </Option>
                                                                  ))}
                                                              </Select>
                                                          </div>
                                                      </div>
                                                  ))
                                                : null}
                                        </div>
                                    ) : (
                                        <div style={{ margin: "0 13px 8px 13px" }}>
                                            <label>{t("filters.tags")}</label>
                                            <div>
                                                <Select
                                                    mode="multiple"
                                                    onDropdownVisibleChange={loadTags}
                                                    defaultActiveFirstOption={false}
                                                    onChange={handleConversationLabelFilter}
                                                    style={{ width: "100%" }}
                                                    placeholder={t("filters.filterByLabel")}
                                                    showSearch={true}
                                                    filterOption={(input, option) =>
                                                        option.props.children
                                                            .toLowerCase()
                                                            .indexOf(input.toLowerCase()) >= 0
                                                    }
                                                >
                                                    {conversationLabels.map((label) => (
                                                        <Option key={label.id} value={label.id}>
                                                            {label.value}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </div>
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            margin: "0 13px 8px 13px",
                                            justifyContent: "space-between",
                                            display: "flex",
                                        }}
                                    >
                                        {renderSortByButton()}

                                        <Button
                                            onClick={() => setOrderDirection((prev) => !prev)}
                                            type={orderDirection ? "primary" : "ghost"}
                                            size="small"
                                            style={{ height: "32px" }}
                                        >
                                            {t("filters.reverse")}
                                            <SwapOutlined style={{ transform: "rotate(90deg)" }} />
                                        </Button>
                                        <div
                                            className="filter-waiting-time"
                                            onClick={() => handleWaitingFilter()}
                                        >
                                            <span className="filter-waiting-time-text">
                                                {t("filters.filterWaiting")}
                                            </span>
                                            <Switch checked={filters.isWaiting} size="small" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            height: `calc(
                                ${sectionRef?.current?.clientHeight}px - 
                                ${filtersDomHeight}px - 
                                ${headerRef?.current?.clientHeight}px - 18px)`,
                            overflow: "auto",
                        }}
                    >
                        {serverError ? (
                            <Result
                                status="warning"
                                title={t("chatListError")}
                                extra={
                                    <>
                                        <p>{t("chatListErrorDesc")}</p>
                                        <Button onClick={() => loadChats()}>{t("Recargar")}</Button>
                                    </>
                                }
                            />
                        ) : (
                            <>
                                {chatsAreLoading ? (
                                    <div style={{ marginTop: ".5rem" }}>
                                        {Array.from(Array(5).keys()).map((i) => (
                                            <ChatItemSkeleton key={i} />
                                        ))}
                                    </div>
                                ) : (
                                    <List
                                        rowKey={(item) => item.id}
                                        itemLayout="horizontal"
                                        dataSource={chats}
                                        renderItem={(item) => (
                                            <ChatItem
                                                chat={item}
                                                selected={item.id === selectedChatId}
                                                openChatCb={openChatCb}
                                                optionsCb={handleChatOptions}
                                                handleModalCb={handleModal}
                                                getChatDescriptionCb={getChatDescription}
                                            />
                                        )}
                                    >
                                        {chatsPaginator.hasNext && (
                                            <LoadMore
                                                key="loadMore"
                                                onClick={changePage}
                                                style={{ marginTop: "5px", marginBottom: "0px" }}
                                            />
                                        )}
                                        {chatList.length === 0 && searchOlderChats && (
                                            <div className="no-results-flex">
                                                <span className="no-results-text">
                                                    {t("filters.noResultsInTimeFrame")}{" "}
                                                    {formatDatetime(filters.startDatetime)} -{" "}
                                                    {formatDatetime(filters.endDatetime)}
                                                </span>
                                            </div>
                                        )}
                                        {!chatsPaginator.hasNext &&
                                            searchOlderChats &&
                                            chatList.length !== 0 && (
                                                <div className="no-results-flex">
                                                    <span className="no-results-text">
                                                        {t("filters.noMoreResultsToShow")}{" "}
                                                        {formatDatetime(filters.startDatetime)} -{" "}
                                                        {formatDatetime(filters.endDatetime)}
                                                    </span>
                                                </div>
                                            )}
                                        {!chatsPaginator.hasNext && searchOlderChats && (
                                            <LoadMoreWithExtendedDate
                                                key="loadMoreWithExtendedDate"
                                                onClick={handleLoadOlderChats}
                                                style={{ marginTop: "5px", marginBottom: "0px" }}
                                            />
                                        )}
                                    </List>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
export default ChatList;
